import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApprovalChainsEnabled } from '@/hooks/useApprovalChainsEnabled';
import { usePermission } from '@/hooks/usePermission';
import { useRequirentePortal } from '@/hooks/useRequirentePortal';
import { useScopedTypeLabels } from '@/hooks/useScopedTypeLabels';
import { cn } from '@/lib/cn';
import {
  isNavGroup,
  type NavEntry,
  type NavItem,
  SIDEBAR_SECTIONS,
} from './sidebarConfig';

const EXPANDED_GROUPS_KEY = 'cv2:sidebar-groups-expanded';

function readExpandedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(EXPANDED_GROUPS_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return typeof parsed === 'object' && parsed != null ? parsed : {};
  } catch {
    return {};
  }
}

function itemVisible(
  item: NavItem,
  actions: Set<string>,
  chainsEnabled: boolean,
): boolean {
  if (!actions.has(item.permission)) {
    return false;
  }
  if (item.requiresApprovalChains && !chainsEnabled) {
    return false;
  }
  return true;
}

function filterEntries(
  entries: NavEntry[],
  actions: Set<string>,
  chainsEnabled: boolean,
): NavEntry[] {
  const result: NavEntry[] = [];
  for (const entry of entries) {
    if (isNavGroup(entry)) {
      const children = entry.children.filter((child) => itemVisible(child, actions, chainsEnabled));
      if (children.length > 0) {
        result.push({ ...entry, children });
      }
      continue;
    }
    if (itemVisible(entry, actions, chainsEnabled)) {
      result.push(entry);
    }
  }
  return result;
}

function resolveNavLabel(to: string, defaultLabel: string, labels: ReturnType<typeof useScopedTypeLabels>): string {
  if (to === '/entities') {
    return labels.entityLabelPlural;
  }
  if (to === '/associations') {
    return labels.associationLabelPlural;
  }
  return defaultLabel;
}

function pathMatches(to: string, pathname: string, search: string): boolean {
  const [path, query] = to.split('?');
  if (pathname !== path && !pathname.startsWith(`${path}/`)) {
    return false;
  }
  if (!query) {
    return pathname === path || (path !== '/documents' && pathname.startsWith(`${path}/`));
  }
  return search.includes(query);
}

interface SidebarNavProps {
  collapsed?: boolean;
  onNavigate?: () => void;
  className?: string;
}

export function SidebarNav({ collapsed = false, onNavigate, className }: SidebarNavProps) {
  const { actions } = usePermission();
  const { isRequirenteOnly, requirenteHomePath } = useRequirentePortal();
  const typeLabels = useScopedTypeLabels();
  const chainsEnabled = useApprovalChainsEnabled();
  const location = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(readExpandedGroups);

  const toggleGroup = useCallback((key: string) => {
    setExpanded((current) => {
      const next = { ...current, [key]: !current[key] };
      localStorage.setItem(EXPANDED_GROUPS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const sections = useMemo(
    () => SIDEBAR_SECTIONS.map((section) => ({
      ...section,
      items: filterEntries(section.items, actions, chainsEnabled),
    })).filter((section) => section.items.length > 0),
    [actions, chainsEnabled],
  );

  const renderLeaf = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      title={collapsed ? resolveNavLabel(item.to, item.label, typeLabels) : undefined}
      onClick={onNavigate}
      className={({ isActive }) => cn(
        'mb-1 flex items-center gap-2 rounded-md py-2 text-sm transition-colors',
        collapsed ? 'justify-center px-2' : 'px-3',
        (isActive || pathMatches(item.to, location.pathname, location.search))
          ? 'bg-accent text-accent-foreground'
          : 'hover:bg-accent/50',
      )}
    >
      <item.icon className="h-4 w-4 shrink-0" />
      {!collapsed && (
        <span className="truncate">{resolveNavLabel(item.to, item.label, typeLabels)}</span>
      )}
    </NavLink>
  );

  const renderGroup = (groupKey: string, label: string, GroupIcon: NavItem['icon'] | undefined, children: NavItem[]) => {
    const childActive = children.some((child) =>
      pathMatches(child.to, location.pathname, location.search),
    );
    const isOpen = expanded[groupKey] ?? childActive;

    if (collapsed) {
      return children.map(renderLeaf);
    }

    return (
      <div key={groupKey} className="mb-1">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => toggleGroup(groupKey)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent/50',
            childActive && 'text-foreground',
          )}
        >
          {GroupIcon && <GroupIcon className="h-4 w-4 shrink-0" />}
          <span className="flex-1 truncate text-left">{label}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 transition-transform', isOpen && 'rotate-180')} />
        </button>
        {isOpen && (
          <div className="ml-3 border-l border-border pl-2">
            {children.map(renderLeaf)}
          </div>
        )}
      </div>
    );
  };

  if (isRequirenteOnly) {
    return (
      <nav className={cn('flex-1 overflow-y-auto px-2 py-4', className)}>
        <NavLink
          to={requirenteHomePath}
          title={collapsed ? 'Mis equipos' : undefined}
          onClick={onNavigate}
          className={({ isActive }) => cn(
            'mb-1 flex items-center gap-2 rounded-md py-2 text-sm transition-colors',
            collapsed ? 'justify-center px-2' : 'px-3',
            isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
          )}
        >
          <span className="truncate">{collapsed ? 'Eq.' : 'Mis equipos'}</span>
        </NavLink>
        {actions.has('export.run') && (
          <NavLink
            to="/exports"
            title={collapsed ? 'Exportaciones' : undefined}
            onClick={onNavigate}
            className={({ isActive }) => cn(
              'mb-1 flex items-center gap-2 rounded-md py-2 text-sm transition-colors',
              collapsed ? 'justify-center px-2' : 'px-3',
              isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
            )}
          >
            <span className="truncate">{collapsed ? 'Exp.' : 'Exportaciones'}</span>
          </NavLink>
        )}
      </nav>
    );
  }

  return (
    <nav className={cn('flex-1 overflow-y-auto px-2 py-4', className)}>
      {sections.map((section) => (
        <div key={section.title} className="mb-6">
          {!collapsed && (
            <div className="mb-2 px-2 text-xs uppercase tracking-wide text-muted-foreground">
              {section.title}
            </div>
          )}
          {section.items.map((entry) => {
            if (isNavGroup(entry)) {
              return renderGroup(
                `${section.title}-${entry.label}`,
                entry.label,
                entry.icon,
                entry.children,
              );
            }
            return renderLeaf(entry);
          })}
        </div>
      ))}
    </nav>
  );
}
